export interface CompetitorResearch {
  origin?: string;
  runId?: string;
  rationale?: string;
  confidence?: 'low' | 'medium' | 'high';
  sourceRefs?: string[];
  contextRevision?: number;
  assessedAt?: string;
  contentHash?: string;
  stale?: boolean;
  manualPositioning?: string[];
}
export interface CompetitorAIInsights {
  lastAnalyzed?: string;
  research?: CompetitorResearch | null;
  [key: string]: unknown;
}
export interface CompetitorPositioning {
  priceQuality: 'low' | 'medium' | 'high' | 'unknown';
  geoScope: 'local' | 'regional' | 'national' | 'international' | 'unknown';
  marketSize: number | null;
  aiInsights?: CompetitorAIInsights | null;
}

export const isCompetitorResearchStale = (competitor: Pick<CompetitorPositioning, 'aiInsights'>) =>
  competitor.aiInsights?.research?.stale === true;

export function hasManualPositioning(
  competitor: Pick<CompetitorPositioning, 'aiInsights'>,
  field: 'priceQuality' | 'geoScope' | 'marketSize'
): boolean {
  const fields = competitor.aiInsights?.research?.manualPositioning;
  return Array.isArray(fields) && fields.includes(field);
}

/** Read current positioning without erasing retained historical or human-edited values. */
export function currentCompetitorPosition(competitor: CompetitorPositioning) {
  const { priceQuality, geoScope } = competitor;
  if (priceQuality === 'unknown' || geoScope === 'unknown') return null;
  const stale = isCompetitorResearchStale(competitor);
  if (
    stale &&
    (!hasManualPositioning(competitor, 'priceQuality') ||
      !hasManualPositioning(competitor, 'geoScope'))
  )
    return null;
  return {
    priceQuality,
    geoScope,
    marketSize:
      stale && !hasManualPositioning(competitor, 'marketSize') ? null : competitor.marketSize,
  };
}

/** Combine only server-confirmed manual-field evidence from the same retained AI interpretation. */
export function mergeManualPositioningEvidence(
  current: CompetitorAIInsights | null | undefined,
  incoming: CompetitorAIInsights | null | undefined,
  preserveCurrent = false
) {
  const selected = preserveCurrent || incoming === undefined ? current : incoming;
  const other = preserveCurrent || incoming === undefined ? incoming : current;
  const basis = selected?.research,
    previous = other?.research;
  if (
    !basis ||
    !previous ||
    basis.origin !== 'ai' ||
    previous.origin !== 'ai' ||
    ['runId', 'contextRevision', 'contentHash', 'assessedAt', 'rationale'].some(
      (field) =>
        basis[field as keyof CompetitorResearch] !== previous[field as keyof CompetitorResearch]
    )
  )
    return selected;
  const allowed = ['priceQuality', 'geoScope', 'marketSize'];
  return {
    ...selected,
    research: {
      ...basis,
      manualPositioning: [
        ...new Set(
          [
            ...(Array.isArray(basis.manualPositioning) ? basis.manualPositioning : []),
            ...(Array.isArray(previous.manualPositioning) ? previous.manualPositioning : []),
          ].filter((field) => allowed.includes(field))
        ),
      ],
    },
  };
}
